/**
 * Rechtliches: Impressum, Datenschutz, Nutzungsbedingungen, Speicher und
 * Lizenzen.
 *
 * Die Texte beschreiben, was die App tatsaechlich tut (Cloudflare als
 * Hosting, Firebase fuer Anmeldung und Daten, kein Tracking). Aendert sich
 * daran etwas – neuer Dienst, Analyse, Werbung –, muessen sie mit.
 *
 * ANGABEN DES BETREIBERS: in OPERATOR eintragen. Solange ein Feld leer ist,
 * zeigt das Impressum dort deutlich "noch nicht eingetragen" statt etwas
 * zu erfinden. Kein Ersatz fuer eine Rechtsberatung.
 */
import { button, h } from '../ui/dom.ts';

export const OPERATOR = {
  /** Vor- und Nachname (bzw. Name der Band und vertretungsberechtigte Person). */
  name: '',
  /** Anschrift, eine Zeile je Eintrag: Strasse, PLZ Ort, Land. */
  address: [] as string[],
  email: '',
  /** Optional. */
  phone: '',
};

/** Adressen der Apps, erscheinen in den Texten. */
const SITE = 'leadsheet.schnittstellerot.com';
const CLOUD_SITE = 'cloud.schnittstellerot.com';
/** Stand der Texte. */
const UPDATED = 'September 2026';

export interface LegalDoc {
  id: string;
  title: string;
  summary: string;
  body: () => Node[];
}

function missing(what: string): HTMLElement {
  return h('span', { class: 'legal__missing', text: `${what} – noch nicht eingetragen` });
}

const p = (text: string): HTMLElement => h('p', { text });
const h3 = (text: string): HTMLElement => h('h3', { text });
const ul = (...items: string[]): HTMLElement => h('ul', {}, ...items.map((t) => h('li', { text: t })));

/** Name und Anschrift des Betreibers, auch fuer "Verantwortlicher" im Datenschutz. */
function operatorBlock(): HTMLElement {
  return h(
    'p',
    {},
    OPERATOR.name ? h('strong', { text: OPERATOR.name }) : missing('Name'),
    ...(OPERATOR.address.length > 0 ? OPERATOR.address.flatMap((line) => [h('br'), line]) : [h('br'), missing('Anschrift')]),
  );
}

function contactBlock(): HTMLElement {
  return h(
    'p',
    {},
    'E-Mail: ',
    OPERATOR.email ? h('a', { text: OPERATOR.email, attrs: { href: `mailto:${OPERATOR.email}` } }) : missing('E-Mail'),
    ...(OPERATOR.phone ? [h('br'), `Telefon: ${OPERATOR.phone}`] : []),
  );
}

export const LEGAL_DOCS: LegalDoc[] = [
  {
    id: 'impressum',
    title: 'Impressum',
    summary: 'Wer hinter der App steht',
    body: () => [
      h3('Angaben zum Betreiber'),
      operatorBlock(),
      h3('Kontakt'),
      contactBlock(),
      h3('Verantwortlich für den Inhalt'),
      p(`${OPERATOR.name || 'Der oben genannte Betreiber'}, Anschrift wie oben.`),
      h3('Hinweis zu Inhalten der Nutzer'),
      p(
        'Songs, Texte, Bilder, Dateien und Notizen in den Apps stammen von den jeweiligen Nutzerinnen und Nutzern. ' +
          'Hinweise auf rechtswidrige Inhalte bitte an die oben genannte Adresse – sie werden umgehend geprüft und gegebenenfalls entfernt.',
      ),
      h3('Haftung für Links'),
      p('Für Inhalte externer Seiten, auf die verwiesen wird, sind ausschließlich deren Betreiber verantwortlich.'),
      h('p', { class: 'hint', text: `Stand: ${UPDATED}` }),
    ],
  },
  {
    id: 'datenschutz',
    title: 'Datenschutzerklärung',
    summary: 'Welche Daten wo gespeichert werden',
    body: () => [
      p(
        `Diese Erklärung gilt für die Web-Apps von Schnittstelle Rot: „Lead Sheets“ unter ${SITE} und „Schnittstelle Rot Cloud“ unter ${CLOUD_SITE}. ` +
          'Kurz gesagt: Die Apps speichern nur, was für die Arbeit nötig ist. Es gibt keine Werbung, keine Analyse-Werkzeuge und kein Tracking.',
      ),

      h3('1. Verantwortlicher'),
      operatorBlock(),
      contactBlock(),

      h3('2. Aufruf der Seite (Hosting)'),
      p(
        'Die App wird über Cloudflare Workers ausgeliefert (Cloudflare, Inc., 101 Townsend St., San Francisco, CA 94107, USA). ' +
          'Beim Aufruf verarbeitet Cloudflare technisch notwendige Verbindungsdaten wie IP-Adresse, Zeitpunkt, aufgerufene Datei und Browserkennung, ' +
          'um die Seite auszuliefern und vor Angriffen zu schützen.',
      ),
      p('Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (sicherer, stabiler Betrieb).'),

      h3('3. Konto und Anmeldung'),
      p(
        'Für die Anmeldung wird Firebase Authentication genutzt (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland). ' +
          'Gespeichert werden E-Mail-Adresse, der gewählte Anzeigename, ein verschlüsseltes Passwort bzw. bei „Mit Google anmelden“ die von Google übermittelten Kontodaten (Name, E-Mail), ' +
          'sowie Zeitpunkte der Registrierung und Anmeldung.',
      ),
      p(
        'Neue Konten werden erst nach Freigabe durch einen Administrator aktiv. Dafür sehen Administratoren Name, E-Mail-Adresse und Rolle der Mitglieder.',
      ),
      p('Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Bereitstellung der App für die Band).'),

      h3('4. Inhalte der Band'),
      p(
        'Songs, Lead Sheets, Setlists, Veröffentlichungen, Band-Bibel, Zeichnungen, Fotos, Tonaufnahmen, Einbände und persönliche Einstellungen ' +
          '(z. B. Design, eigenes Hintergrundbild) werden in Google Cloud Firestore gespeichert (Google Ireland Limited, s. o.), damit alle freigegebenen Mitglieder damit arbeiten können. ' +
          'Persönliche Einstellungen und als „Privat“ markierte Einbände und Cover sieht nur die jeweilige Person. ' +
          'Als „Privat“ markierte Projekte sind derzeit eine Markierung: freigeschaltete Mitglieder der Band können sie technisch lesen. Ein echter Zugriffsschutz je Projekt ist in Arbeit.',
      ),
      p('Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.'),

      h3('4a. Schnittstelle Rot Cloud (Testbetrieb)'),
      p(
        'Dateien, die in der Cloud unter „Schnittstelle Rot Cloud“ abgelegt werden, speichert Google Cloud Firestore (Google Ireland Limited, s. o.) in einem eigenen Bereich je Konto. ' +
          'Nur die Person, der das Konto gehört, kann diese Dateien lesen, ändern und löschen – auch Administratoren nicht. ' +
          'Dateien unter „Dieses Gerät“ verlassen das Gerät nicht. Im Testbetrieb gelten Grenzen von 25 MB je Datei und 500 MB je Konto.',
      ),
      p('Gelöschte Dateien liegen im Papierkorb, bis sie dort endgültig gelöscht werden. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.'),

      h3('4b. Schnittstelle Rot AI'),
      p(
        'Die AI arbeitet nur, wenn du einen Auftrag ausdrücklich mit „Ausführen“ abschickst. Dann gehen der Auftrag und die Inhalte, die du ausdrücklich angehängt hast, ' +
          'über den Server von Schnittstelle Rot (Cloudflare, s. o.) an den gewählten Anbieter: Anthropic (Claude), OpenAI (ChatGPT) oder Perplexity – alle mit Sitz in den USA. ' +
          'Welcher Anbieter es ist, steht vor dem Senden in der App. Andere Inhalte, Dateien oder Kontodaten werden nicht übermittelt.',
      ),
      p(
        'Schnittstelle Rot speichert Aufträge und Antworten nicht. Das Server-Protokoll enthält nur Metadaten (Zeitpunkt, Konto-Kennung, Anbieter, Dauer, Anzahl der Tokens); ' +
          'das Nutzungsprotokoll in der App bleibt auf dem Gerät. Wie lange der Anbieter Daten aufbewahrt, regeln dessen eigene Bedingungen.',
      ),

      h3('5. Speicher auf dem Gerät'),
      p(
        'Damit die App schnell ist und offline funktioniert, legt sie Daten im Browser ab (IndexedDB, Local Storage, Offline-Speicher des Service Workers). ' +
          'Diese Speicher sind für den Betrieb unbedingt erforderlich; es werden keine Cookies zu Werbe- oder Analysezwecken gesetzt. ' +
          'Mehr dazu unter „Speicher und Cookies“.',
      ),
      p('Rechtsgrundlage: § 25 Abs. 2 Nr. 2 TDDDG sowie Art. 6 Abs. 1 lit. b DSGVO.'),

      h3('6. Spracherkennung im Teleprompter'),
      p(
        'Nur wenn „Sprache folgen“ eingeschaltet wird, nutzt die App die Spracherkennung des Browsers. ' +
          'Das Mikrofon wird erst nach Zustimmung im Browser verwendet. Die Erkennung übernimmt der Browser bzw. dessen Hersteller (bei Safari: Apple), ' +
          'teils auf dessen Servern. Die App selbst speichert keine Tonaufnahmen daraus.',
      ),
      p('Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung durch Einschalten); jederzeit durch Ausschalten widerrufbar.'),

      h3('7. Übermittlung in Drittländer'),
      p(
        'Cloudflare und Google können Daten auch in den USA verarbeiten. Beide Unternehmen sind nach dem EU-US Data Privacy Framework zertifiziert; ' +
          'zusätzlich gelten die EU-Standardvertragsklauseln.',
      ),

      h3('8. Speicherdauer'),
      p(
        'Konto und Inhalte bleiben gespeichert, solange das Konto besteht. Auf Wunsch wird das Konto gelöscht; von einem Mitglied angelegte Inhalte, ' +
          'die die Band weiter braucht (z. B. gemeinsame Songs), können bestehen bleiben. Verbindungsdaten beim Hosting löscht Cloudflare nach kurzer Zeit.',
      ),

      h3('9. Deine Rechte'),
      p('Du hast jederzeit das Recht auf'),
      ul(
        'Auskunft über deine gespeicherten Daten (Art. 15 DSGVO)',
        'Berichtigung (Art. 16 DSGVO)',
        'Löschung (Art. 17 DSGVO)',
        'Einschränkung der Verarbeitung (Art. 18 DSGVO)',
        'Datenübertragbarkeit (Art. 20 DSGVO) – z. B. über Einstellungen → Daten und Backup',
        'Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)',
        'Widerruf einer Einwilligung mit Wirkung für die Zukunft (Art. 7 Abs. 3 DSGVO)',
      ),
      p('Außerdem kannst du dich bei einer Datenschutz-Aufsichtsbehörde beschweren (Art. 77 DSGVO). Eine formlose Nachricht an die oben genannte Adresse genügt für alle Anliegen.'),

      h('p', { class: 'hint', text: `Stand: ${UPDATED}` }),
    ],
  },
  {
    id: 'nutzung',
    title: 'Nutzungsbedingungen',
    summary: 'Regeln für die Nutzung in der Band',
    body: () => [
      h3('1. Zweck'),
      p('Lead Sheets ist eine private Arbeits-App für die Mitglieder der Band. Ein Anspruch auf Zugang oder ständige Verfügbarkeit besteht nicht.'),
      h3('2. Zugang'),
      p(
        'Ein Konto wird erst nach Freigabe durch einen Administrator aktiv. Administratoren können Rollen vergeben, Konten sperren und wieder entsperren. ' +
          'Zugangsdaten sind geheim zu halten.',
      ),
      h3('3. Inhalte'),
      p(
        'Wer Songs, Texte, Bilder, Noten oder Tonaufnahmen hochlädt, versichert, dazu berechtigt zu sein. ' +
          'Viele Liedtexte und Noten sind urheberrechtlich geschützt: Sie dürfen hier nur für die eigene Probenarbeit der Band genutzt und nicht veröffentlicht werden.',
      ),
      p('Rechtswidrige oder beleidigende Inhalte sind nicht erlaubt und werden entfernt.'),
      h3('4. Daten sichern'),
      p('Die App speichert zuverlässig in der Cloud, trotzdem gilt: wichtige Inhalte regelmäßig über Einstellungen → Daten und Backup exportieren.'),
      h3('5. Haftung'),
      p('Die App wird unentgeltlich und ohne Gewähr bereitgestellt. Gehaftet wird nur für Vorsatz und grobe Fahrlässigkeit sowie nach zwingenden gesetzlichen Vorschriften.'),
      h('p', { class: 'hint', text: `Stand: ${UPDATED}` }),
    ],
  },
  {
    id: 'speicher',
    title: 'Speicher und Cookies',
    summary: 'Was auf deinem Gerät liegt',
    body: () => [
      p('Die App setzt keine Werbe- oder Analyse-Cookies. Deshalb gibt es auch kein Cookie-Banner. Auf dem Gerät liegen nur technisch notwendige Daten:'),
      ul(
        'IndexedDB: eine Kopie deiner Band-Daten, damit Lead Sheets offline und schnell funktioniert – und in der Cloud die Dateien unter „Dieses Gerät“ sowie deine Design-Einstellungen.',
        'Firebase-Anmeldung: die Sitzung, solange „Angemeldet bleiben“ gewählt ist.',
        'Local Storage: kleine Vorlieben wie gewählte Sprache im Teleprompter, Stift oder Finger beim Zeichnen, zuletzt geöffneter Reiter – und ein Fehlerprotokoll für die Diagnose, das das Gerät nie verlässt.',
        'Service Worker: die Dateien der App selbst, damit sie ohne Netz startet.',
      ),
      p('All das lässt sich in den Browser-Einstellungen löschen (Safari: Einstellungen → Safari → Erweitert → Website-Daten). Danach meldest du dich einfach neu an.'),
    ],
  },
  {
    id: 'lizenzen',
    title: 'Lizenzen',
    summary: 'Verwendete Software und Bilder',
    body: () => [
      h3('Software'),
      p('Firebase JavaScript SDK – © Google LLC, Apache License 2.0.'),
      p('Alles Übrige (Oberfläche, Akkord-Darstellung, Zeichenebene) ist für diese App geschrieben.'),
      h3('Bilder'),
      p('Die Cover der Beispiel-Veröffentlichungen und die Hintergründe sind für diese App erzeugt, die Stoff-Texturen der Buch-Einbände stellt der Betreiber bereit. Eigene Bilder bleiben Eigentum der Person, die sie hochlädt.'),
      p('Die Beispiel-Songs enthalten nur Akkordfolgen, keine geschützten Texte oder Melodien.'),
    ],
  },
];

export function findLegalDoc(id: string): LegalDoc | undefined {
  return LEGAL_DOCS.find((d) => d.id === id);
}

/** Inhalt eines Dokuments, fuer Einstellungen und das Blatt beim Login. */
export function legalBody(doc: LegalDoc): HTMLElement {
  return h('div', { class: 'legal' }, ...doc.body());
}

/** Zeigt ein Dokument als Blatt ueber allem – auch vor der Anmeldung. */
export function openLegal(id: string): void {
  const doc = findLegalDoc(id);
  if (!doc) return;
  const modal = h(
    'div',
    { class: 'modal', attrs: { role: 'dialog', 'aria-modal': 'true' } },
    h(
      'div',
      { class: 'modal__panel' },
      h(
        'div',
        { class: 'modal__head' },
        h('h2', { class: 'modal__title', text: doc.title }),
        button('Schließen', () => modal.remove(), { class: 'btn btn--small' }),
      ),
      h('div', { class: 'modal__body' }, legalBody(doc)),
    ),
  );
  modal.addEventListener('click', (ev) => {
    if (ev.target === modal) modal.remove();
  });
  document.body.append(modal);
}

/** Kleine Linkzeile (Impressum · Datenschutz …), z. B. unter dem Login. */
export function legalLinks(): HTMLElement {
  return h(
    'nav',
    { class: 'legal-links', attrs: { 'aria-label': 'Rechtliches' } },
    ...LEGAL_DOCS.filter((d) => d.id !== 'lizenzen').map((d) =>
      h('button', { class: 'legal-links__item', text: d.title, attrs: { type: 'button' }, on: { click: () => openLegal(d.id) } }),
    ),
  );
}
